<template>
  <div
    class="w-full relative border-1 rounded-2xl px-[10px] laptop:px-[20px] pb-[10px] laptop:pb-[25px] largeDesktop:px-[30px] largeDesktop:pb-[35px] pt-[35px] border-gray-300">
      <DetailBackButton fallback="/work-order/universal-quotes" />
    <!-- actions: first 3 as pills, the rest in the ⋮ menu (ActionBar), on the card's top edge like S&R / RQ -->
    <ActionBar v-if="barActions.length" :actions="barActions"
      trigger-class="group w-[40px] h-[40px] rounded-full flex items-center justify-center transition-all duration-300 border-0 bg-transparent p-0"
      class="w-full laptop:px-2.5 absolute -top-[21px] right-0">
    <template #pill="{ action, run, narrow }">
      <button type="button" :disabled="action.disabled"
        class="group flex items-center rounded-full gap-2 py-1.5 pl-3 pr-2 font-roboto font-normal text-[15px] largeDesktop:text-[17px] h-[40px] border-1 whitespace-nowrap transition duration-300 disabled:cursor-not-allowed disabled:opacity-[0.5]"
        :class="narrow ? 'bg-[color:var(--pill)] border-[color:var(--pill)] text-[#fff]' : 'bg-[#fff] border-[color:var(--pill)] text-[color:var(--pill)] enabled:hover:bg-[color:var(--pill)] enabled:hover:text-[#fff]'"
        :style="{ '--pill': action.color }" @click="run(action)">
        {{ action.label }}
        <span
          class="w-[26px] h-[26px] rounded-full flex items-center justify-center bg-white shadow-[0px_0px_15px_-7px_#333]">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M0 0h24v24H0z" fill="none" />
            <path fill="none" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              :stroke="action.color" d="M4 12H6.5M14 18L20 12L14 6M20 12H9.5" />
          </svg>
        </span>
      </button>
    </template>
    <template #trigger>
      <img src="../../assets/images/3-dots-icon.png" alt=""
        class="w-full h-full object-contain group-hover:hidden block [@media(max-width:1023px)]:!hidden">
      <img src="../../assets/images/3-dots-icon-hover.png" alt=""
        class="w-full h-full object-contain group-hover:block hidden [@media(max-width:1023px)]:!block">
    </template>
    </ActionBar>
    <!-- partner replacement banner: same pink pill as Edit Quote, centred at the top of the page card -->
    <div v-if="checkNoteAlert()" class="flex justify-center mb-[20px]">
      <AlertNote />
    </div>
    <div class="w-full flex-col laptop:flex-row flex gap-[20px] largeDesktop:gap-[30px]">
      <div class="w-full flex flex-col gap-[20px]">
        <div
          class="flex flex-col items-start w-full shadow-[0px_0px_15px_-2px_#ccc] rounded-[20px] p-[15px] bg-white">
          <!-- details header -->
          <!-- Figma "Quote Details - Option 1": purple "Details", then one green "Quote Amount" pill (= the payable total)
               (the partner logo is in the partner box under the details) -->
          <div
            :class="['flex flex-wrap justify-between items-center gap-[10px] w-full pb-[10px]']">
            <div class="flex justify-start items-center gap-1">
              <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
              <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
              <p class="text-[20px] font-semibold font-roboto pl-2 m-0 text-[#9336B4]">
                Details
              </p>
            </div>
            <div class="flex flex-wrap items-center justify-end gap-[6px] laptop:gap-[10px] ml-auto">
              <template v-if="amounts">
                <!-- one pill in every case (normal and partner): the payable total, labelled "Quote Amount" -->
                <div class="flex items-center gap-[8px] font-roboto mobile:bg-[#EFEFEF]/50 laptop:bg-transparent rounded-full mobile:p-[5px] laptop:p-0 mobile:pl-[10px] laptop:pl-0 justify-between mobile:w-full laptop:w-auto">
                  <span class="text-[14px] largeDesktop:text-[16px] text-[#000000]">Quote Amount:</span>
                  <span
                    class="px-[10px] py-[5px] rounded-full text-[14px] largeDesktop:text-[16px] font-bold text-white bg-[#54B435]">
                    ${{ $helper.formatAmount(amounts.payable) }}
                  </span>
                </div>
              </template>
            </div>
          </div>
          <!-- heading box -->
          <div
            class="flex mobile:flex-col tablet:flex-row justify-between items-center w-full gap-1 !pl-[10px] mobile:p-2.5 tablet:p-[5px] shadow-[0px_0px_15px_-2px_#ccc] rounded-[10px]">
            <h4
              class="mobile:text-center tablet:text-left mobile:w-full tablet:w-auto mobile:border-1 tablet:border-0 border-b border-[#EFEFEF] mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-medium font-roboto m-0 mobile:pb-2.5 tablet:pb-0 text-[#5E366A]">
              {{ $helper.isEmpty(quoteDetail?.job.title) ? '-' : quoteDetail?.job.title }}
            </h4>
            <div
              class="flex items-center mobile:justify-between tablet:justify-end mobile:w-full tablet:w-auto gap-2.5 mobile:pt-2.5 tablet:pt-0">
              <h4
                class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-medium font-roboto m-0 text-[#333333]">
                Quote Status:
              </h4>
              <span
                class="mobile:text-[14px] text-[12px] largeDesktop:text-[14px] font-medium font-roboto py-[4px] px-[18px] rounded-[5px] border-1"
                :style="{
                  borderColor: getStatusClass(quoteDetail.status.technician.text),
                  backgroundColor: `${getStatusClass(quoteDetail.status.technician.text)}0D`,
                  color: getStatusClass(quoteDetail.status.technician.text)
                }">{{
                  $helper.isEmpty(quoteDetail.status.technician.text) ? "-" :
                    formatStatus(quoteDetail.status.technician.text)
                }}</span>
            </div>
          </div>
          <!-- QR box just for mobile / tablet (the QR card in the right column is laptop+), same as S&R Job Details -->
          <div v-if="qr?.qrcodeUrl" class="w-full bg-white pt-[15px] mobile:flex laptop:hidden justify-between items-center">
            <div>
              <span class="m-0 text-[12px] text-[#F67280]">Technician</span>
              <p class="m-0 text-[14px] font-semibold text-[#333333]">{{ technicianName }}</p>
            </div>
            <img :src="qr.qrcodeUrl"
              class="w-[60px] h-[60px] object-contain rounded-[5px] p-[4px] shadow-[0px_0px_17px_-2px_#ccc]" alt="QR code">
          </div>
          <!-- details -->
          <div class="w-full grid tablet:grid-cols-3 gap-y-[5px] tablet:gap-y-[17px] mt-[20px]">
            <div class="tablet:col-span-3">
              <p
                class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[18px] font-roboto leading-tight font-normal mb-0 text-[#333333]">
                {{ quoteDetail?.address_text ?? '' }}
              </p>
            </div>
            <div>
              <span
                class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Appointment
                ID:</span>
              <p
                class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal mb-0 text-[#333333]">
                <!-- source appointment (job_source slug "appointment": id = sourceable_id); other sources have none -->
                {{ quoteDetail.job_source?.slug === 'appointment' && Number(quoteDetail.job_source?.id) > 0 ?
                  quoteDetail.job_source.id : "-" }}
              </p>
            </div>
            <div>
              <span
                class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Quote
                ID:</span>
              <p
                class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal mb-0 text-[#333333]">
                {{ $helper.isEmpty(quoteDetail.id) ? "-" : quoteDetail.id }}
              </p>
            </div>
            <div>
              <span
                class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Job
                Number:</span>
              <p
                class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal mb-0 text-[#333333]">
                {{ $helper.isEmpty(quoteDetail.partner_info.job_number) ? "-" : quoteDetail.partner_info.job_number }}
              </p>
            </div>
            <div>
              <span
                class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Trade
                Type /
                Service:</span>
              <p
                class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal mb-0 text-[#333333]">
                {{ quoteDetail?.trade.title + " / " + quoteDetail?.service_type?.title }}
              </p>
            </div>
            <div>
              <span
                class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Schedule
                Date:</span>
              <p
                class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal mb-0 text-[#333333]">
                {{ $helper.isEmpty(quoteDetail.schedule_datetime) ? ' - ' :
                  convertTimsStampToDate(quoteDetail.schedule_datetime) }}
              </p>
            </div>
            <div v-if="quoteDetail.created_at">
              <span
                class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-roboto font-normal text-[#EA8A8A]">Created
                Date:</span>
              <p
                class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-roboto font-normal mb-0 text-[#333333]">
                {{ convertTimsStampToDate(quoteDetail.created_at) }}
              </p>
            </div>
          </div>
        </div>
        <QuoteDocumentsCard v-if="quoteDetail?.id" :quote-id="quoteDetail.id" />
      </div>
      <div
        class="laptop:min-w-[320px] largeDesktop:min-w-[350px] w-full laptop:max-w-[320px] largeDesktop:max-w-[350px] flex flex-col gap-[20px]">
        <!-- separate Consumer / Partner cards, same as the S&R detail -->
        <ConsumerDetailsCard v-if="quoteDetail.consumer" :consumer="quoteDetail.consumer"
          :members-to="Number(quoteDetail.consumer.user_id) > 0 ? { name: 'quote-members', params: { id: quoteDetail.id } } : null" />
        <!-- partner (allied) quote: claim id, plan, partner phone, remarks, replacement note
             (Android IndependentQuoteDetailFragment.setDetails "partner_layout") -->
        <div v-if="isAlliedPartner"
          class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] p-[15px] bg-white flex flex-col gap-[10px] font-roboto">
          <div class="flex items-center gap-1 pb-[5px]">
            <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
            <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
            <p class="text-[18px] laptop:text-[20px] font-semibold pl-2 m-0 text-[#333333]">Partner Details</p>
          </div>
          <div class="flex items-center gap-[10px]">
            <img v-if="partner.company_logo" :src="partner.company_logo" :alt="partner.company_name || 'Partner'"
              class="w-[40px] h-[40px] object-contain flex-shrink-0">
            <div class="flex-1 min-w-0">
              <p class="m-0 mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-medium text-[#333333] break-words">
                {{ partner.formatted_claim_text || '-' }}
              </p>
              <a v-if="partner.phone" :href="'tel:' + partner.phone"
                class="block text-[13px] text-[#666666] no-underline">{{ formatPhone(partner.phone) }}</a>
            </div>
          </div>
          <div class="grid gap-[10px]">
            <div>
              <span class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-normal text-[#EA8A8A]">Plan Name:</span>
              <p class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-normal mb-0 text-[#333333]">
                {{ partner.plan_name || '-' }}</p>
            </div>
            <div v-if="partner.remarks">
              <span class="mobile:text-[14px] laptop:text-[13px] largeDesktop:text-[14px] font-normal text-[#EA8A8A]">Remarks:</span>
              <p class="mobile:text-[16px] laptop:text-[14px] largeDesktop:text-[16px] font-normal mb-0 text-[#333333] whitespace-pre-line">
                {{ partner.remarks }}</p>
            </div>
          </div>
          <!-- Call pill, same as the Consumer Details card -->
          <div v-if="partner.phone" class="flex items-center gap-[10px] pt-[15px] border-t-1 border-[#EFEFEF]">
            <ChatChooser :size="36" :show-chat="false" :phone="partner.phone" :consumer-name="partner.company_name || ''" />
          </div>
        </div>
        <noteBox v-if="quoteAppointmentId" :appointment-id="quoteAppointmentId" />
        <!-- non-partner quotes: the quote's own notes (manual-quote/get-notes), so the right column is never empty -->
        <!-- Figma UQ "Quote - Details" (option 1): right card titled "Internal Notes" (dot + bar) -->
        <div v-else class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] p-[15px] bg-white">
          <div class="flex items-center gap-1 pb-[15px]">
            <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
            <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
            <p class="text-[18px] laptop:text-[20px] font-semibold font-roboto pl-2 m-0 text-[#333333]">Internal Notes
            </p>
          </div>
          <quoteNoteBox :hide-title="true" />
        </div>
        <MapCard v-if="Number(quoteDetail.address_lat)" :lat="quoteDetail.address_lat" :lng="quoteDetail.address_lng"
          :address="quoteDetail.address_text || ''" />
        <!-- QR (submitted quote), laptop+; same card as S&R Job Details -->
        <div v-if="qr?.qrcodeUrl"
          class="shadow-[0px_0px_10px_0px_#0000001A] rounded-[20px] p-[15px] mobile:hidden laptop:block bg-white">
          <div class="flex items-center gap-1 pb-[15px]">
            <div class="w-[8px] h-[8px] rounded-full bg-[#A084E8]" />
            <div class="w-[35px] h-[8px] rounded-full bg-[#A084E8]" />
            <p class="text-[18px] laptop:text-[20px] font-semibold font-roboto pl-2 m-0 text-[#333333]">QR Code</p>
          </div>
          <img :src="qr.qrcodeUrl" class="mx-auto block w-[140px] h-[140px] object-contain" alt="QR code">
          <p class="m-0 mt-2 text-[13px] font-roboto text-[#333333] text-center">
            Present this <span class="text-[#FE5000]">QR code</span> to the customer to view the quote details.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DetailBackButton from "@/components/partialComponent/DetailBackButton.vue";
import AlertNote from "@/components/alert_note/AlertNote.vue";
import ConsumerDetailsCard from "@/components/partialComponent/ConsumerDetailsCard.vue";
import ChatChooser from "@/components/chat/ChatChooser.vue";
import { mapActions, mapGetters } from "vuex";
import moment from "moment";
import noteBox from "../notes/apptNoteModal.vue";
import quoteNoteBox from "../notes/noteBox.vue";
import ActionBar from "../partialComponent/ActionBar.vue";
import MapCard from "../partialComponent/MapCard.vue";
import { uqApi } from "../../services/uqApi";
import QuoteDocumentsCard from "./QuoteDocumentsCard.vue";

export default {
  components: {
    DetailBackButton,
    MapCard,
    AlertNote,
    ConsumerDetailsCard,
    ChatChooser,
    noteBox,
    quoteNoteBox,
    ActionBar,
    QuoteDocumentsCard,
  },
  props: ["actionButtons", "actionButtonClicked", "isQrEnabled",],
  data() {
    return {
      center: {
        lat: 0,
        lng: 0,
      },
      markers: [
        {
          position: {
            lat: 0,
            lng: 0,
          },
        }, // Along list of clusters
      ],
      // header pill: { payable: total } from the quote summary (null → pill hidden)
      amounts: null,
      amountsKey: null,
      // qrCode/job-detail (job_source iq) once the quote is submitted → { qrcodeUrl, jobStateUrl }
      qr: null,
      qrFor: null,
    };
  },
  computed: {
    ...mapGetters(["quoteDetail", "apptDetail"]),

    // ONE ordered list for ActionBar: first 3 as pills, the rest in the ⋮ menu
    barActions() {
      return (this.actionButtons || []).map(action => ({
        ...action,
        key: action.title,
        label: action.title,
        onClick: () => this.actionButtonClicked(action.title),
      }));
    },

    technicianName() {
      const t = this.quoteDetail?.technician || {};
      return t.first_name || t.full_name || "-";
    },
    partner() {
      return this.quoteDetail?.partner_info || {};
    },
    // Android: partner_info.status and is_allied
    isAlliedPartner() {
      return this.isPartnerQuote && String(this.partner.is_allied) === "1";
    },
    isPartnerQuote() {
      return (this.quoteDetail?.partner_info?.id > 0 && this.quoteDetail?.partner_info?.status != '0') || false
    },
    // Partner quotes come from an appointment (job_source.id); its internal notes are shown here
    quoteAppointmentId() {
      return this.isPartnerQuote ? (this.quoteDetail?.job_source?.id || null) : null
    }

  },

  watch: {
    // the detail refreshes every 45s; re-read the amounts only when the quote itself changed
    quoteDetail: function () {
      this.loadAmounts();
      this.loadQr();
    },
    // QuoteDetail sets it from the same detail, after this watcher may have run
    isQrEnabled() {
      this.loadQr();
    },

    apptDetail: function () {
      // this.markers[0].position.lat = this.apptDetail.lat;
      // this.markers[0].position.lng = this.apptDetail.lng;

      // this.center.lat = this.apptDetail.lat;
      // this.center.lng = this.apptDetail.lng;
    },
  },

  mounted() {
    if (!this.$helper.isEmpty(this.quoteDetail)) {
      this.markers[0].position.lat = this.quoteDetail.address_lat;
      this.markers[0].position.lng = this.quoteDetail.address_lng;

      this.center.lat = this.quoteDetail.address_lat;
      this.center.lng = this.quoteDetail.address_lng;
    } else {
      if (this.quoteDetail != null && this.isPartnerQuote) {
        this.getAppointmentDetailsAPICall({
          id: this.quoteDetail.job_source.id,
        });
      }
    }
    this.loadAmounts();
    this.loadQr();
  },

  methods: {
    ...mapActions(["getAppointmentDetailsAPICall"]),
    // (loadAmounts) the "Quote Amount" pill = payable total: the detail payload has no totals, so read the summary View Quote uses
    // Android: QR Code once the quote is submitted (is_quote_submitted); loaded once per quote
    async loadQr() {
      const q = this.quoteDetail;
      if (!q?.id || !this.isQrEnabled) {
        this.qr = null;
        this.qrFor = null;
        return;
      }
      if (this.qrFor === q.id) return;
      this.qrFor = q.id;
      const res = await uqApi.qrCode(q.id);
      this.qr = res.ok ? res.body : null;
    },
    async loadAmounts() {
      const q = this.quoteDetail;
      // same rule as View Quote (Android): any state but Pending has a quote; Pending only once it has items
      // (a completed quote's detail sends no items, but its summary still has the totals)
      const hasQuote = q?.status?.technician?.status != 0 || q?.items?.length > 0;
      if (!q?.id || !hasQuote) {
        this.amounts = null;
        this.amountsKey = null;
        return;
      }
      const key = [q.id, q.updated_at, q.is_quote_submitted, q.items?.length, q.status?.technician?.status].join("|");
      if (key === this.amountsKey) return;
      this.amountsKey = key;
      const res = await uqApi.summary(q.id, this.isPartnerQuote);
      if (key !== this.amountsKey) return;
      this.amounts = res.ok && res.body
        ? { payable: Number(res.body.total) || 0 }
        : null;
    },
    getStatusClass(status) {
      const statusClasses = {
        'Started': '#00BCD4',
        'Installation Started': '#65B741',
        'Pending Checkout': '#429538',
        'Pending': '#F59E0B',
        'Pending Approval': '#8B5E3C',
        'Suspended': '#DC2626',
        'Payment Completed': '#65B741',
        'Completed': '#10B981',
        'Order Closed': '#686D76',
        'Scheduled': '#0284C7',
        'Cancelled': '#B91C1C',
      };
      return statusClasses[status] || 'bg-gray-500';
    },
    formatStatus(status) {
      return status
        ?.toLowerCase()
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    // last 10 digits through the (xxx) xxx-xxxx mask (Android getFormattedNumber); a shorter number fills it partly
    formatPhone(value) {
      const d = String(value || "").replace(/\D/g, "").slice(-10);
      if (d.length <= 3) return d;
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}` + (d.length > 6 ? `-${d.slice(6)}` : "");
    },
    checkNoteAlert() {
      if (this.quoteDetail &&
        this.quoteDetail.partner_info
        && this.quoteDetail.partner_info.show_replacement_opportunity_badge == '1'
        && this.quoteDetail.partner_info.status == '1'
        && this.quoteDetail.partner_info.company_name != 'Vivint'
        && this.quoteDetail.partner_info.replacement_opportunity_number?.length > 0) {
        return true
      } else {
        return false
      }
    },
    openGoogleMaps() {
      const desLat = this.quoteDetail.address_lat ?? 0;
      const desLng = this.quoteDetail.address_lng ?? 0;
      const googleMapsUrl = `https://maps.google.com/?q=${desLat},${desLng}`;

      // Check if the Google Maps app is installed
      if (navigator.platform.indexOf("Win") > -1) {
        // Windows platform, open in a new window
        window.open(googleMapsUrl, "_blank");
      } else {
        // Non-Windows platform, try to open in the maps app
        window.location.href = googleMapsUrl;
        //  window.location.href =`geo:0,0?q=${encodeURIComponent(destination)}`;
      }
    },
    convertTimsStampToDate(val) {
      return moment.unix(val).format("MM/DD/YYYY - hh:mm A");
    }
  },
};
</script>
